import {
  AuctionBatchInfo,
  CanceledShipment,
  DocumentNotFound,
  DocumentSection,
  ExpiredQuote,
  GenericInfo,
  InvoiceDownload,
  ItemInfo,
  Loader,
  StoreInfo,
  PickUpInfo,
  PlainLayout,
  QuotePriceAndExpireDate,
  RecipientInfo,
  ShippingOption,
} from '@/components/shared';
import { ShipmentStep } from '@/components/shared/ShipmentStep';

import type { IExtraServices, IItem, IShippingOption, IStoreInfo } from '@/types';
import {
  Box,
  Button,
  Container,
  Flex,
  Grid,
  Heading,
  Icon,
  Link,
  Radio,
  RadioGroup,
  Stack,
  Text,
  Tooltip,
  useBoolean,
} from '@chakra-ui/react';
import { Trans, useTranslation } from 'react-i18next';
import { IoIosArrowDropleft } from 'react-icons/io';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuoteApi } from '../api';
import { SummaryBox } from '../components';
import {
  AvailableServicesSection,
  DefaultServiceRow,
  ExtraServiceCard,
  ExtraServiceRow,
} from '../components/ExtraServices';
import { useExtraServices, useFetchQuote, useQuoteSummary } from '../hooks';
import { usePaymentMethods } from '../hooks/usePaymentMethods';
import { formatDate } from '@/utils';
import { MbeInfo, MbeProfessionalManagement } from '@/components/icons';

interface IStatePath {
  returnPath: string | null;
}

// Removed static generatePaymentUrl

export const QuoteDetails: React.FC = (): JSX.Element => {
  const { i18n, t } = useTranslation();
  const [isUpdating, setUpdating] = useBoolean(false);
  const quoteApi = useQuoteApi();
  const { id } = useParams();
  const navigate = useNavigate();
  const { errorType, fetch: updateQuote, isLoading, quote } = useFetchQuote(id);
  const { state } = useLocation();

  const {
    auctionHouse,
    createdAt,
    customer: user,
    extraServices: exsvr,
    item,
    quoteExpiryDate,
    recipient: shipping,
    total,
  } = quote ?? {};

  let shippingOptions: IShippingOption[] = [];

  if (item?.shippingOptions) {
    const seen = new Set();

    shippingOptions = item.shippingOptions.filter((el) => {
      const isDuplicate = seen.has(el.service);
      seen.add(el.service);
      return !isDuplicate;
    });
  }

  let returnPath;

  if (state) {
    ({ returnPath } = state as IStatePath);
  }

  const paddingTop = returnPath === 'false' ? '30px' : '0px';

  const useQuoteSummaryParams = { auctionHouse, item: item as IItem, shipping, user, id };

  const {
    auctionCode,
    auctionDate,
    auctionHouseAddress,
    auctionName,
    batchCode,
    itemInfo,
    shippingAddress,
    shippingRecipient,
  } = useQuoteSummary(useQuoteSummaryParams);

  const { added, available } = useExtraServices(exsvr);

  const manageServices = async (esId: string, isIncluded = false): Promise<void> => {
    if (quote && id && exsvr) {
      try {
        setUpdating.on();
        const toUpdate = { ...exsvr[esId as keyof IExtraServices], isIncluded };
        const newEs = { ...exsvr, [esId]: toUpdate };
        const newQuote = { ...quote, extraServices: newEs };
        await quoteApi.update(newQuote);
        updateQuote();
      } catch (error) {
        console.error(error);
      } finally {
        setUpdating.off();
      }
    }
  };

  const manageShippingOptions = async (id: string): Promise<void> => {
    if (quote) {
      try {
        setUpdating.on();
        const newShippingOption = shippingOptions.find((opt) => opt.service === id);

        const newQuote = { ...quote, shipmentService: newShippingOption?.service };
        await quoteApi.update(newQuote);
        updateQuote();
      } catch (error) {
        console.error(error);
      } finally {
        setUpdating.off();
      }
    }
  };

  const addExtraService = (id: string): void => void manageServices(id, true);
  const removeExtraService = (id: string): void => void manageServices(id);

  const {
    getPaymentUrl,
    isLoading: isFetchingMethods,
    isPaymentEnabled,
    paymentMethods,
    selectedPaymentMethod,
    setSelectedPaymentMethod,
  } = usePaymentMethods(id, quote);

  const shouldDisplayAvailableServices =
    isPaymentEnabled && available.some((service) => service.isVisible);

  const title = quote?.type === 'quote' ? t('QUOTE_DETAILS.TITLE') : t('SHIPMENT_DETAILS.TITLE');

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const hasQuoteTotal = quote?.total !== null && quote?.status === 'QUOTE_REQUESTED';

  const titleWithId =
    quote?.type === 'quote'
      ? t('QUOTE_DETAILS.NUMBER', { id })
      : t('SHIPMENT_DETAILS.NUMBER', { id });

  if (isLoading || isUpdating) return <Loader />;

  if (errorType === 'Quote Expired')
    return (
      <PlainLayout>
        <ExpiredQuote />
      </PlainLayout>
    );

  if (errorType === 'Not Found' || errorType === 'Unauthorized')
    return (
      <PlainLayout>
        <DocumentNotFound />
      </PlainLayout>
    );

  if (quote?.status === 'SHIPMENT_CANCELED') {
    return (
      <PlainLayout>
        <CanceledShipment />
      </PlainLayout>
    );
  }

  const storeInfo: IStoreInfo | undefined = quote?.store;

  return (
    <PlainLayout>
      <Box bg="brand.sand" paddingBottom={16} paddingTop={{ base: 0, md: 2 }} w="full">
        <Container maxW="8xl" mt={paddingTop} px={{ base: 0, md: 8 }} py={{ base: 0, lg: 0 }}>
          {returnPath !== 'false' && (
            <Button color="brand.red.light" px={2} onClick={(): void => navigate(-1)}>
              <Icon
                as={IoIosArrowDropleft}
                color="brand.red.light"
                h={10}
                my={{ base: 4, md: 10 }}
                w={10}
              />
            </Button>
          )}
          <DocumentSection // eslint-disable-next-line sonarjs/no-duplicate-string
            containerProps={{ alignItems: 'flex-start', py: 0, px: 0, mt: 0, gap: '40px' }}
            headingProps={{ mt: 0 }}
            id="quotes"
            title={title}
          >
            <Flex
              bg="white"
              direction="column"
              minW={{ base: 0, md: '100%' }}
              px={{ base: 4, md: 44 }}
              py={16}
            >
              <Heading display={{ base: 'block', md: 'none' }} mb={2} textAlign="center">
                {title}
              </Heading>
              <Flex
                alignItems="center"
                direction={{ base: 'column', md: 'row' }}
                gap={8}
                justifyContent="space-between"
                mb={10}
                w="full"
              >
                <GenericInfo
                  date={t('QUOTE_DETAILS.CREATION_DATE', {
                    date: formatDate(createdAt ?? '', i18n.language),
                    interpolation: { escapeValue: false },
                  })}
                  showMoreLabel={t('ACTIONS.SHOW_MORE')}
                  title={titleWithId}
                />
                {quote && (
                  <QuotePriceAndExpireDate
                    expireDate={t('QUOTE_DETAILS.EXPIRATION_DATE', {
                      date: formatDate(quoteExpiryDate, i18n.language),
                      interpolation: { escapeValue: false },
                    })}
                    quoteStatus={quote.status}
                  />
                )}
              </Flex>
              <Flex
                alignItems={{ base: 'center', md: 'flex-start' }}
                direction={{ base: 'column', md: 'row' }}
                gap={8}
                w="full"
              >
                <SummaryBox>
                  <Grid gap={6} templateColumns={{ base: 'repeat(1, 1fr)', sm: 'repeat(3, 1fr)' }}>
                    <RecipientInfo {...shippingAddress} fullname={shippingRecipient} />
                    <PickUpInfo {...auctionHouseAddress} ahName={auctionName} />
                    {storeInfo && <StoreInfo store={storeInfo} />}
                    {quoteExpiryDate && <ItemInfo {...itemInfo} />}
                    <AuctionBatchInfo
                      auctionCode={auctionCode}
                      auctionDate={t('QUOTE_DETAILS.AUCTION_DATE', {
                        date: formatDate(auctionDate, i18n.language),
                        interpolation: { escapeValue: false },
                      })}
                      batchCode={batchCode}
                    />
                  </Grid>
                </SummaryBox>
              </Flex>

              {!quoteExpiryDate && (
                <Flex direction="column" gap={4}>
                  <Text fontSize={{ base: '24px', md: 'h3.md' }}>
                    {t('QUOTE_DETAILS.SERVICES_TITLE')}
                  </Text>
                  <Text>{t('QUOTE_DETAILS.SERVICES_QUOTE_REQUESTED')}</Text>
                </Flex>
              )}

              {quote?.type === 'shipment' && quote.trackingMbe && (
                <ShipmentStep trackingNumber={quote.trackingMbe} />
              )}

              {quote && quote.status !== 'QUOTE_REQUESTED' && (
                <>
                  <Flex
                    alignItems={{ base: 'flex-start', md: 'flex-start' }}
                    borderBottom={
                      shippingOptions.length === 1 || quote.type === 'shipment' ? '1px' : '0px'
                    }
                    borderColor={
                      shippingOptions.length === 1 || quote.type === 'shipment'
                        ? 'black'
                        : 'transparent'
                    }
                    direction="column"
                    gap={4}
                    mb={10}
                    pb={shippingOptions.length === 1 || quote.type === 'shipment' ? 10 : 0}
                    w="full"
                  >
                    {!hasQuoteTotal && quote.status === 'QUOTE_PENDING' && (
                      <>
                        <Text fontSize={{ base: '24px', md: 'h3.md' }}>
                          {t('QUOTE_DETAILS.SERVICES_TITLE')}
                        </Text>
                        <Text>
                          {quote.isPersonalizedOffer
                            ? t('QUOTE_DETAILS.IS_PERSONALIZED_SERVICES_DESC')
                            : t('QUOTE_DETAILS.SERVICES_DESC')}
                        </Text>
                      </>
                    )}
                  </Flex>

                  {quote.type === 'quote' &&
                    quote.status === 'QUOTE_PENDING' &&
                    shippingOptions.length === 1 && (
                      <ShippingOption
                        courier={shippingOptions[0].courier}
                        service={shippingOptions[0].service}
                      />
                    )}
                  {quote.type === 'shipment' && quote.shipmentService && (
                    <ShippingOption
                      courier={quote.shipmentCourier}
                      service={quote.shipmentService}
                    />
                  )}
                  {quote.type === 'quote' && shippingOptions.length > 1 && (
                    <RadioGroup
                      borderColor="black"
                      borderTop="1px"
                      pt={{ base: 4, md: 12 }}
                      value={quote.shipmentService}
                      // eslint-disable-next-line @typescript-eslint/no-misused-promises
                      onChange={async (value): Promise<void> => manageShippingOptions(value)}
                    >
                      <Stack direction="column" gap={8}>
                        {shippingOptions.map((shippingOption) => {
                          const key = `${shippingOption.service}-${shippingOption.courier}`;
                          return (
                            <Radio key={key} value={shippingOption.service} variant="mbe">
                              <ShippingOption
                                courier={shippingOption.courier}
                                radio={true}
                                service={shippingOption.service}
                              />
                            </Radio>
                          );
                        })}
                      </Stack>
                    </RadioGroup>
                  )}

                  {!hasQuoteTotal && (
                    <Flex flexDirection="column" my={8} w="full">
                      <DefaultServiceRow
                        additionalInfo={t(
                          'QUOTE_DETAILS.PROFESSIONAL_MANAGEMENT_SERVICE_ADDITIONAL_INFO'
                        )}
                        description={t('QUOTE_DETAILS.PROFESSIONAL_MANAGEMENT_SERVICE_DESC')}
                        icon={MbeProfessionalManagement}
                        id={'default-professionalManagement'}
                        name={t('QUOTE_DETAILS.PROFESSIONAL_MANAGEMENT_SERVICE_NAME')}
                      />
                      {added.map(({ id, isVisible, ...rest }) => (
                        <ExtraServiceRow
                          id={id}
                          key={`added-${id}`}
                          {...rest}
                          isShipmentDoc={quote.type === 'shipment'}
                          isVisible={isVisible}
                          onClick={(): void => removeExtraService(id)}
                        />
                      ))}
                    </Flex>
                  )}
                  <Flex flexDirection="column" w="full">
                    {quote.type === 'quote' && !hasQuoteTotal && (
                      <Flex flexDirection="row-reverse" w="full">
                        <Text fontSize={{ base: 'h3.base', md: 'h3.md' }}>
                          {t('QUOTE_DETAILS.TOTAL')}
                          <Text as="span" fontWeight="bold" ml={2}>
                            {total}&euro;
                          </Text>
                          <Tooltip
                            bgColor="brand.black"
                            label={t('QUOTE_DETAILS.TOTAL_TOOLTIP')}
                            p={4}
                          >
                            <MbeInfo
                              _hover={{ color: 'white', bg: 'brand.red.light' }}
                              color="brand.red.light"
                              fontSize="28"
                              ml={2}
                              rounded="full"
                              sx={{ verticalAlign: 'sub' }}
                            />
                          </Tooltip>
                        </Text>
                      </Flex>
                    )}
                    {isPaymentEnabled && (
                      <Flex
                        flexDirection={{ base: 'column', md: 'row-reverse' }}
                        flexWrap="wrap"
                        gap={4}
                        my={10}
                        w="full"
                      >
                        <Flex
                          flexDirection={{ base: 'column', md: 'row' }}
                          flexWrap="nowrap"
                          gap={4}
                          w="full"
                        >
                          {isFetchingMethods ? (
                            <Loader />
                          ) : (
                            <Flex direction="row" justifyContent="end" w="full">
                              <Flex alignItems="end" direction="column">
                                <RadioGroup
                                  className="radiog-right-aligment"
                                  value={selectedPaymentMethod ?? ''}
                                  onChange={setSelectedPaymentMethod}
                                >
                                  <Stack direction="column" gap={6}>
                                    {paymentMethods.map((method) => (
                                      <Radio key={method.id} value={method.id} variant="mbe">
                                        <Flex alignItems="center" gap={4}>
                                          <Box
                                            alt={method.description}
                                            as="img"
                                            h="32px"
                                            src={method.image}
                                          />
                                          <Text fontSize="lg" fontWeight="medium">
                                            {method.description}
                                          </Text>
                                        </Flex>
                                      </Radio>
                                    ))}
                                  </Stack>
                                </RadioGroup>
                                <Button
                                  as="a"
                                  disabled={!selectedPaymentMethod}
                                  href={
                                    selectedPaymentMethod
                                      ? getPaymentUrl(selectedPaymentMethod)
                                      : '#'
                                  }
                                  mt={10}
                                  variant="mbePrimary"
                                  w={{ base: 'full', md: 'max-content' }}
                                >
                                  {t('ACTIONS.PAYMENT')}
                                </Button>
                              </Flex>
                            </Flex>
                          )}
                        </Flex>
                        {quote.isPersonalizedOffer && (
                          <Text mr={{ base: 0, md: 28 }} mt={{ base: 2, md: 0 }}>
                            <Trans
                              components={{
                                url: (
                                  <Link
                                    color="brand.red.light"
                                    fontWeight="bold"
                                    href={storeInfo?.email && `mailto:${storeInfo.email}`}
                                  />
                                ),
                              }}
                              i18nKey={'QUOTE_DETAILS.IS_PERSONALIZED_OFFER_DESC'}
                            />
                          </Text>
                        )}
                      </Flex>
                    )}
                  </Flex>
                </>
              )}
              {shouldDisplayAvailableServices && (
                <AvailableServicesSection>
                  <Flex flexWrap="wrap" gap={6} w="full">
                    {available.map((es) => (
                      <Flex
                        flexBasis={{ base: 'full', md: '300px', lg: '250px', xl: '300px' }}
                        key={es.id}
                      >
                        <ExtraServiceCard
                          btnLabel={t('ACTIONS.ADD')}
                          desc={t(es.description)}
                          icon={es.icon}
                          isVisible={es.isVisible}
                          label={t(es.label)}
                          name={t(es.name)}
                          onClick={(): void => addExtraService(es.id)}
                        />
                      </Flex>
                    ))}
                  </Flex>
                </AvailableServicesSection>
              )}
              {quote?.type === 'shipment' && quote.status !== 'SHIPMENT_REQUESTED' && (
                <InvoiceDownload id={quote.id} />
              )}
            </Flex>
          </DocumentSection>
        </Container>
      </Box>
      <style>
        {`
          .radiog-right-aligment .chakra-radio {
            justify-content: right;
          }
          .radiog-right-aligment .chakra-radio .chakra-radio__control {
            order: 2;
            margin-left: 1em;
          }
          .radiog-right-aligment .chakra-radio .chakra-radio__label {
            order: 1;
          }
          .radiog-right-aligment .chakra-radio .chakra-radio__label p {
            order: 1;
          }
          .radiog-right-aligment .chakra-radio .chakra-radio__label img {
            order: 2;
          }
        `}
      </style>
    </PlainLayout>
  );
};
